import React, { useEffect, useState, useCallback } from 'react';
import { Table, Button, Modal, Form, Input, Select, Popconfirm, message, Space, Tag, Tabs, Checkbox, Card, Spin, Grid } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, SafetyOutlined } from '@ant-design/icons';
import { kullanicilarAPI, yetkilerAPI } from '../services/api';

const SAYFA_GRUPLARI = [
  {
    grup: 'Genel',
    sayfalar: [
      { key: '/', label: 'Anasayfa' },
      { key: '/ajanda', label: 'Ajanda' },
    ],
  },
  {
    grup: 'Mutfak İşlemleri',
    sayfalar: [
      { key: '/malzemeler', label: 'Malzemeler' },
      { key: '/urunler', label: 'Ürünler' },
      { key: '/uretimler', label: 'Üretim' },
      { key: '/paketleme', label: 'Paketleme' },
    ],
  },
  {
    grup: 'Finansal İşlemler',
    sayfalar: [
      { key: '/satislar', label: 'Satışlar' },
      { key: '/alislar', label: 'Alışlar' },
      { key: '/odemeler', label: 'Ödemeler' },
      { key: '/tahsilatlar', label: 'Tahsilatlar' },
      { key: '/virman', label: 'Virman' },
      { key: '/cariler', label: 'Cariler' },
      { key: '/musteriler', label: 'Müşteriler' },
      { key: '/kasalar', label: 'Kasalar' },
    ],
  },
  {
    grup: 'Yönetim',
    sayfalar: [
      { key: '/kullanicilar', label: 'Kullanıcılar' },
      { key: '/satis-takvimi', label: 'Satış Takvimi' },
      { key: '/raporlama', label: 'Raporlama' },
      { key: '/log-kayitlari', label: 'Log Kayıtları' },
    ],
  },
  {
    grup: 'Ayarlar',
    sayfalar: [
      { key: '/ayarlar/liste-fiyatlari', label: 'Liste Fiyatları' },
      { key: '/ayarlar/gelir-tipleri', label: 'Seçenek Ayarları' },
    ],
  },
];

function KullaniciListesi() {
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [aramaMetni, setAramaMetni] = useState('');
  const [yetkiSecenekleri, setYetkiSecenekleri] = useState([]);
  const [form] = Form.useForm();

  const durumlar = [
    { value: 1, label: 'Aktif' },
    { value: 0, label: 'Pasif' },
  ];

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await kullanicilarAPI.getAll();
      setData(res.data);
    } catch (err) {
      message.error('Kullanicilar yuklenemedi: ' + (err.response?.data?.hata || err.message));
    } finally {
      setLoading(false);
    }
  };

  const fetchYetkiler = async () => {
    try {
      const res = await yetkilerAPI.getAll();
      setYetkiSecenekleri(res.data.map(y => ({ value: y.id, label: y.rol })));
    } catch (err) {
      message.error('Yetkiler yuklenemedi: ' + (err.response?.data?.hata || err.message));
    }
  };

  useEffect(() => {
    fetchData();
    fetchYetkiler();
  }, []);

  const handleSubmit = async (values) => {
    try {
      if (editingRecord) {
        const payload = { ...values };
        if (!payload.sifre) delete payload.sifre;
        await kullanicilarAPI.update(editingRecord.id, payload);
        message.success('Kullanici guncellendi');
      } else {
        await kullanicilarAPI.create(values);
        message.success('Kullanici eklendi');
      }
      setModalOpen(false);
      setEditingRecord(null);
      form.resetFields();
      fetchData();
    } catch (err) {
      message.error((editingRecord ? 'Kullanici guncellenemedi: ' : 'Kullanici eklenemedi: ') + (err.response?.data?.hata || err.message));
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    form.setFieldsValue({
      ad: record.ad,
      email: record.email,
      yetki_id: record.yetki_id,
      durum: record.durum,
    });
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await kullanicilarAPI.remove(id);
      message.success('Kullanici silindi');
      fetchData();
    } catch (err) {
      message.error('Kullanici silinemedi: ' + (err.response?.data?.hata || err.message));
    }
  };

  const filteredData = aramaMetni
    ? data.filter(k => (k.ad && k.ad.toLowerCase().includes(aramaMetni.toLowerCase())) || (k.email && k.email.toLowerCase().includes(aramaMetni.toLowerCase())))
    : data;

  const strSorter = (key) => (a, b) => (a[key] || '').localeCompare(b[key] || '', 'tr');

  const columns = [
    { title: 'Ad', dataIndex: 'ad', key: 'ad', sorter: strSorter('ad') },
    { title: 'Email', dataIndex: 'email', key: 'email', sorter: strSorter('email'), responsive: ['md'] },
    { title: 'Rol', dataIndex: 'rol', key: 'rol', sorter: strSorter('rol'),
      render: (rol) => {
        const renk = rol === 'admin' ? 'red' : rol === 'yonetici' ? 'orange' : 'blue';
        return <Tag color={renk}>{rol}</Tag>;
      }
    },
    { title: 'Durum', dataIndex: 'durum', key: 'durum', responsive: ['sm'],
      render: (durum) => <Tag color={durum ? 'green' : 'default'}>{durum ? 'Aktif' : 'Pasif'}</Tag>
    },
    {
      title: 'Islem', key: 'islem',
      render: (_, record) => (
        <Space size={4}>
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>{!isMobile && 'Duzenle'}</Button>
          <Popconfirm title="Bu kullaniciyi silmek istediginize emin misiniz?" onConfirm={() => handleDelete(record.id)} okText="Evet" cancelText="Hayir">
            <Button danger size="small" icon={<DeleteOutlined />}>{!isMobile && 'Sil'}</Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'nowrap' }}>
        <Space size={6} style={{ flexShrink: 0 }}>
          <Input placeholder="Ara..." prefix={<SearchOutlined />} allowClear onChange={(e) => setAramaMetni(e.target.value)} style={{ width: isMobile ? 110 : 200 }} />
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalOpen(true)} title="Yeni Kullanici">
            {!isMobile && 'Yeni Kullanici'}
          </Button>
        </Space>
      </div>

      <Table columns={columns} dataSource={filteredData} rowKey="id" loading={loading} pagination={{ defaultPageSize: 10, pageSizeOptions: ['10', '20', '50'], showSizeChanger: true, showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} kayit` }} />

      <Modal title={editingRecord ? 'Kullanici Duzenle' : 'Yeni Kullanici'} open={modalOpen} onCancel={() => { setModalOpen(false); setEditingRecord(null); form.resetFields(); }} onOk={() => form.submit()}>
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name="ad" label="Ad Soyad" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="sifre" label="Sifre" rules={editingRecord ? [] : [{ required: true, min: 6, message: 'Sifre en az 6 karakter olmalidir' }]}>
            <Input.Password placeholder={editingRecord ? 'Bos birakirsaniz degismez' : ''} />
          </Form.Item>
          <Form.Item name="yetki_id" label="Rol" rules={[{ required: true }]}>
            <Select options={yetkiSecenekleri} />
          </Form.Item>
          <Form.Item name="durum" label="Durum" initialValue={1}>
            <Select options={durumlar} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

function YetkiYonetimi() {
  const [kullaniciTipleri, setKullaniciTipleri] = useState([]);
  const [seciliTip, setSeciliTip] = useState(null);
  const [izinler, setIzinler] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchKullaniciTipleri = async () => {
    setLoading(true);
    try {
      const res = await yetkilerAPI.getAll();
      const tipler = res.data.map(y => y.rol);
      setKullaniciTipleri(tipler);
      if (tipler.length > 0 && !seciliTip) setSeciliTip(tipler[0]);
    } catch (err) {
      message.error('Yetkiler yuklenemedi: ' + (err.response?.data?.hata || err.message));
    } finally {
      setLoading(false);
    }
  };

  const fetchIzinler = useCallback(async (tip) => {
    if (!tip) return;
    setLoading(true);
    try {
      const res = await yetkilerAPI.getByRol(tip.toLowerCase());
      setIzinler(res.data.izinler || []);
    } catch {
      setIzinler([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchKullaniciTipleri(); }, []);

  useEffect(() => {
    if (seciliTip) fetchIzinler(seciliTip);
  }, [seciliTip, fetchIzinler]);

  const handleToggle = (key) => {
    setIzinler(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
  };

  const handleGrupToggle = (sayfalar) => {
    const keys = sayfalar.map(s => s.key);
    const hepsiVar = keys.every(k => izinler.includes(k));
    if (hepsiVar) {
      setIzinler(prev => prev.filter(k => !keys.includes(k)));
    } else {
      setIzinler(prev => [...new Set([...prev, ...keys])]);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await yetkilerAPI.update(seciliTip.toLowerCase(), izinler);
      message.success(`${seciliTip} yetkileri kaydedildi`);
    } catch (err) {
      message.error('Yetkiler kaydedilemedi: ' + (err.response?.data?.hata || err.message));
    } finally {
      setSaving(false);
    }
  };

  const tumSayfalar = SAYFA_GRUPLARI.flatMap(g => g.sayfalar);
  const handleTumunuSec = () => {
    const tumKeys = tumSayfalar.map(s => s.key);
    const hepsiVar = tumKeys.every(k => izinler.includes(k));
    setIzinler(hepsiVar ? [] : tumKeys);
  };

  if (loading && kullaniciTipleri.length === 0) {
    return <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 16 }}>
        <div style={{ width: 200, flexShrink: 0 }}>
          <div style={{ fontWeight: 600, marginBottom: 8, color: '#666', fontSize: 13 }}>Kullanıcı Tipleri</div>
          {kullaniciTipleri.map(tip => (
            <div
              key={tip}
              onClick={() => setSeciliTip(tip)}
              style={{
                padding: '10px 16px',
                cursor: 'pointer',
                borderRadius: 6,
                marginBottom: 4,
                fontWeight: seciliTip === tip ? 600 : 400,
                background: seciliTip === tip ? '#FFF3E6' : 'transparent',
                color: seciliTip === tip ? '#FF6B00' : '#333',
                border: seciliTip === tip ? '1px solid #FFD8B0' : '1px solid transparent',
                transition: 'all 0.2s',
              }}
            >
              {tip}
            </div>
          ))}
        </div>

        <div style={{ flex: 1 }}>
          {seciliTip && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div>
                  <span style={{ fontSize: 16, fontWeight: 600 }}>{seciliTip}</span>
                  <span style={{ color: '#999', marginLeft: 8, fontSize: 13 }}>
                    {izinler.length} / {tumSayfalar.length} sayfa
                  </span>
                </div>
                <Space>
                  <Button size="small" onClick={handleTumunuSec}>
                    {tumSayfalar.every(s => izinler.includes(s.key)) ? 'Tümünü Kaldır' : 'Tümünü Seç'}
                  </Button>
                  <Button type="primary" onClick={handleSave} loading={saving}>
                    Kaydet
                  </Button>
                </Space>
              </div>

              <Spin spinning={loading}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                  {SAYFA_GRUPLARI.map(grup => {
                    const grupKeys = grup.sayfalar.map(s => s.key);
                    const hepsiVar = grupKeys.every(k => izinler.includes(k));
                    const bazisiVar = grupKeys.some(k => izinler.includes(k)) && !hepsiVar;

                    return (
                      <Card
                        key={grup.grup}
                        size="small"
                        title={
                          <Checkbox
                            checked={hepsiVar}
                            indeterminate={bazisiVar}
                            onChange={() => handleGrupToggle(grup.sayfalar)}
                            style={{ fontWeight: 600 }}
                          >
                            {grup.grup}
                          </Checkbox>
                        }
                        style={{ width: 220 }}
                      >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {grup.sayfalar.map(sayfa => (
                            <Checkbox
                              key={sayfa.key}
                              checked={izinler.includes(sayfa.key)}
                              onChange={() => handleToggle(sayfa.key)}
                            >
                              {sayfa.label}
                            </Checkbox>
                          ))}
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </Spin>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Kullanicilar() {
  return (
    <div>
      <h2>Kullanıcılar</h2>
      <Tabs
        defaultActiveKey="liste"
        items={[
          { key: 'liste', label: 'Kullanıcı Listesi', children: <KullaniciListesi /> },
          { key: 'yetkiler', label: <span><SafetyOutlined /> Yetki Yönetimi</span>, children: <YetkiYonetimi /> },
        ]}
      />
    </div>
  );
}

export default Kullanicilar;
